import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import { getBodyPreviewHtml } from "../../../helpers/metaTemplate";

interface ReadMoreTextProps {
  text: string;
  maxLines: number;
  variant?: "body1" | "body2" | "caption";
  showEmptyMessage?: boolean;
  emptyMessage?: string;
  isFromTemplateCreation?: boolean;
}

export const ReadMoreText: React.FC<ReadMoreTextProps> = ({
  text,
  maxLines,
  variant = "body2",
  showEmptyMessage = true,
  emptyMessage = "",
  isFromTemplateCreation = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldTruncate, setShouldTruncate] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  // Detect if text contains HTML tags
  const containsHtml = /<[a-z][\s\S]*>/i?.test(text);

  // For plain text with newlines, convert to HTML with <br /> tags
  const processedText = containsHtml ? text : text?.replace(/\n/g, "<br />");

  useEffect(() => {
    if (textRef.current) {
      const element = textRef.current;

      const needsTruncation = element.scrollHeight > element.clientHeight + 2;

      setShouldTruncate(needsTruncation);
    }
  }, [text, maxLines]);

  if (!text || text.trim() === "") {
    return showEmptyMessage ? (
      <Typography variant={variant} sx={{ color: "text.secondary" }}>
        {emptyMessage}
      </Typography>
    ) : null;
  }

  return (
    <Box>
      <Typography
        ref={textRef}
        variant={variant}
        sx={{
          whiteSpace: "pre-line",
          display: "-webkit-box",
          WebkitLineClamp: isExpanded ? "unset" : maxLines,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          // lineHeight: isFromTemplateCreation ? 1.1 : 1.4,
          lineHeight: 1.4,
          // Add this to ensure HTML renders correctly
          "& p": { margin: 0 },
          "& br": { display: "block", content: '""', margin: 0 },
        }}
      >
        <span
          dangerouslySetInnerHTML={{
            __html: getBodyPreviewHtml(processedText),
          }}
        />
      </Typography>

      {shouldTruncate && (
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          sx={{
            minWidth: "auto",
            p: 0,
            fontSize: "0.75rem",
            color: "green",
            fontWeight: "bold",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "transparent",
              textDecoration: "underline",
            },
          }}
        >
          {isExpanded ? "Read less" : "Read more"}
        </Button>
      )}
    </Box>
  );
};
